/**
 * Getting the bytes to S3. The real thing, replacing the simulated `run()`.
 *
 * Two shapes, chosen by kind and size, both direct to S3 — nothing here passes
 * through a Lambda:
 *
 *   every photo, and video under the threshold  → one presigned POST
 *   video at or above it → multipart: start, sign a window of parts, PUT, complete
 *
 * D25 makes multipart mandatory for video above the threshold, and the reason
 * is what happens on a walk from the ballroom to the beach: a single POST that
 * dies at minute eleven of thirteen restarts from zero, and the guest tries
 * again, and it dies again. Parts turn that into losing one part.
 */
import {
  MULTIPART_THRESHOLD_BYTES,
  type MultipartCompleteRequest, type MultipartPartUrl,
  type MultipartStartRequest, type MultipartStartResponse,
  type PresignRequest, type PresignResponse,
} from '@mv/contracts';
import { api, uploadPart, uploadPresigned } from './api';

/** How many parts to hold in flight. Same reasoning as the queue's transfer
 *  limit: on a bandwidth-bound link this buys overlap of per-request latency,
 *  not throughput. */
const PART_CONCURRENCY = 3;

/** How many part URLs to ask for at a time. A 500 MB clip is 63 parts and
 *  presigning all of them up front is a large response the guest may never
 *  use, on the connection this exists to protect. */
const SIGN_BATCH = 10;

/** Per part, not per file. A part is ~8 MB, so this is generous. */
const PART_RETRIES = 3;

export interface ProgressSink {
  (fraction: number): void;
}

/**
 * **Video only, and the `kind` argument is the whole point.**
 *
 * This used to take bytes alone, and the 8 MB threshold then caught photos too
 * — but `/photos/multipart/start` accepts `video/mp4` and `video/quicktime` and
 * nothing else, so every photo above the threshold was sent to a video endpoint
 * and answered "That is not a video we can show to everyone." Guests saw it on
 * ordinary photos, mixed in with photos from the same picker selection that
 * went up fine; in ninety days the endpoint was called ten times and returned
 * 400 ten times.
 *
 * A photo never needs this path. It is at most a few megabytes by the time
 * `derivativesFor` is done, and when the browser cannot re-encode it at all the
 * single POST still carries it — which is what D25's "slower, never worse"
 * fallback promises and what the 8 MB cliff was quietly breaking.
 */
export const needsMultipart = (kind: 'photo' | 'video', bytes: number) =>
  kind === 'video' && bytes >= MULTIPART_THRESHOLD_BYTES;

/**
 * The single-POST path. **Every photo takes this**, whatever its size.
 *
 * Usually that is uncontroversial — the derivative is made in the browser and a
 * 2560px WebP is under a megabyte. The case that matters is the other one: when
 * the browser cannot re-encode the file at all it sends the camera original, and
 * this is the path that carries it. Choosing by size alone sent those to the
 * video endpoint instead (D64).
 */
export async function uploadSingle(
  opts: {
    kind: 'photo' | 'video';
    contentType: string;
    primary: Blob;
    thumb: Blob | null;
    durationSec?: number;
    /** Video only. Signed into the upload as S3 metadata so the transcoder can
     *  size its output without upscaling. Ignored for photos. */
    width?: number;
    height?: number;
    onProgress?: ProgressSink;
    signal?: AbortSignal;
  },
): Promise<string> {
  const presigned = await api<PresignResponse>('/photos/presign', {
    method: 'POST',
    json: {
      kind: opts.kind,
      contentType: opts.contentType,
      bytes: opts.primary.size,
      // The thumb's own type, because the browser's encoder may have produced
      // something other than WebP and the policy pins Content-Type (D66).
      thumbContentType: opts.thumb?.type,
      durationSec: opts.durationSec,
      width: opts.width,
      height: opts.height,
    } satisfies PresignRequest,
  });

  // The thumbnail is tiny and goes first, so that if the connection dies
  // mid-upload the tile at least has something to show on retry.
  if (opts.thumb) {
    await uploadPresigned(presigned.thumb, opts.thumb, undefined, opts.signal);
  }
  await uploadPresigned(presigned.primary, opts.primary, opts.onProgress, opts.signal);

  return presigned.photoId;
}

/**
 * The resumable path. Returns the photoId once every part is in and S3 has
 * assembled them.
 *
 * `onProgress` is fed from the sum of all parts rather than from whichever part
 * finished last, so the bar moves smoothly instead of jumping in 8 MB steps.
 */
export async function uploadMultipart(
  opts: {
    file: Blob;
    contentType: string;
    durationSec?: number;
    width?: number;
    height?: number;
    thumb: Blob | null;
    onProgress?: ProgressSink;
    signal?: AbortSignal;
  },
): Promise<string> {
  const start = await api<MultipartStartResponse>('/photos/multipart/start', {
    method: 'POST',
    json: {
      contentType: opts.contentType,
      bytes: opts.file.size,
      // Same reason as the single-POST path: the poster frame is whatever the
      // browser could encode, not necessarily WebP (D66).
      thumbContentType: opts.thumb?.type,
      durationSec: opts.durationSec,
      width: opts.width,
      height: opts.height,
    } satisfies MultipartStartRequest,
  });

  const total = Math.ceil(opts.file.size / start.partSize);
  const done: { partNumber: number; etag: string }[] = [];
  const sentPerPart = new Map<number, number>();

  const report = () => {
    if (!opts.onProgress) return;
    let sent = 0;
    for (const n of sentPerPart.values()) sent += n;
    opts.onProgress(Math.min(1, sent / opts.file.size));
  };

  try {
    // The poster frame goes to the target `start` handed back, which is keyed
    // on the same `photoId` as the clip.
    //
    // This used to ask `/photos/presign` for a target of its own, and that call
    // mints a fresh `photoId` every time — so the frame was uploaded under an id
    // belonging to nothing, the row's `thumb` key 404'd, and the tile fell back
    // to a blank frame. Confirmed in production on the first video ever
    // uploaded: the poster sat at `thumb/mv6panw9-gxt9z7mn` while the row
    // pointed at `thumb/mv6panck-lnwx0q6c` (D65).
    if (opts.thumb) {
      await uploadPresigned(start.thumb, opts.thumb, undefined, opts.signal)
        .catch(() => { /* A missing poster is a blank tile, not a failed upload. */ });
    }

    for (let first = 1; first <= total; first += SIGN_BATCH) {
      const numbers = [];
      for (let n = first; n < first + SIGN_BATCH && n <= total; n += 1) numbers.push(n);

      const { parts } = await api<{ parts: MultipartPartUrl[] }>('/photos/multipart/parts', {
        method: 'POST',
        json: { key: start.key, uploadId: start.uploadId, partNumbers: numbers },
      });

      await pooled(parts.map((part) => async () => {
        const from = (part.partNumber - 1) * start.partSize;
        const slice = opts.file.slice(from, Math.min(from + start.partSize, opts.file.size));

        let lastError: unknown;
        for (let attempt = 0; attempt < PART_RETRIES; attempt += 1) {
          try {
            const etag = await uploadPart(part.url, slice, (f) => {
              sentPerPart.set(part.partNumber, f * slice.size);
              report();
            }, opts.signal);
            done.push({ partNumber: part.partNumber, etag });
            sentPerPart.set(part.partNumber, slice.size);
            report();
            return;
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') throw err;
            lastError = err;
            // Losing one part is the normal case this whole path exists for.
            // Back off a little and have another go before giving up the file.
            await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
          }
        }
        throw lastError;
      }), PART_CONCURRENCY);
    }

    await api<{ ok: true }>('/photos/multipart/complete', {
      method: 'POST',
      json: {
        photoId: start.photoId, uploadId: start.uploadId, key: start.key, parts: done,
      } satisfies MultipartCompleteRequest,
    });

    return start.photoId;
  } catch (err) {
    // Abandoned parts bill as invisible storage until the bucket's three-day
    // lifecycle rule runs. Tell S3 to drop them now; failing to is a cost, not
    // a correctness problem, so it must never mask the original error.
    await api('/photos/multipart/abort', {
      method: 'POST',
      json: { key: start.key, uploadId: start.uploadId },
    }).catch(() => {});
    throw err;
  }
}

async function pooled(tasks: (() => Promise<void>)[], limit: number): Promise<void> {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    for (let i = next++; i < tasks.length; i = next++) await tasks[i]!();
  });
  await Promise.all(workers);
}
